import { supabase } from '../lib/supabase';
import openai from './openaiClient';

import { generateMarketingContractTemplate } from './openaiContractTemplateService';
import { autoReviewContract } from './anthropicContractReviewService';
import { exportContractToPDF } from '../utils/pdfExportService';

/**
 * Workflow Automation Service
 * Handles batch processing, automated follow-ups, and bulk document generation
 */

export const workflowAutomationService = {
  /**
   * Create a new workflow job
   */
  async createWorkflowJob(jobData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('workflow_jobs')?.insert({
          user_id: user?.id,
          job_type: jobData?.jobType,
          job_name: jobData?.jobName,
          total_items: jobData?.totalItems || 0,
          configuration: jobData?.configuration || {}
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        jobType: data?.job_type,
        jobName: data?.job_name,
        status: data?.status,
        totalItems: data?.total_items,
        processedItems: data?.processed_items,
        failedItems: data?.failed_items,
        configuration: data?.configuration,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating workflow job:', error);
      throw error;
    }
  },

  /**
   * Get all workflow jobs for current user
   */
  async getWorkflowJobs(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.jobType) {
        query = query?.eq('job_type', filters?.jobType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(job => ({
        id: job?.id,
        jobType: job?.job_type,
        jobName: job?.job_name,
        status: job?.status,
        totalItems: job?.total_items,
        processedItems: job?.processed_items,
        failedItems: job?.failed_items,
        configuration: job?.configuration,
        results: job?.results,
        errorLog: job?.error_log,
        startedAt: job?.started_at,
        completedAt: job?.completed_at,
        createdAt: job?.created_at,
        updatedAt: job?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching workflow jobs:', error);
      throw error;
    }
  },

  /**
   * Update workflow job status
   */
  async updateWorkflowJobStatus(jobId, status, additionalData = {}) {
    try {
      const updateData = { status, updated_at: new Date()?.toISOString() };

      if (status === 'running' && !additionalData?.startedAt) {
        updateData.started_at = new Date()?.toISOString();
      }

      if (status === 'completed' || status === 'failed') {
        updateData.completed_at = new Date()?.toISOString();
      }

      if (additionalData?.results) {
        updateData.results = additionalData?.results;
      }

      if (additionalData?.errorLog) {
        updateData.error_log = additionalData?.errorLog;
      }

      const { data, error } = await supabase?.from('workflow_jobs')?.update(updateData)?.eq('id', jobId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating workflow job:', error);
      throw error;
    }
  },

  /**
   * Batch Contract Processing
   * Processes multiple contracts with AI review and approval workflow
   */
  async processBatchContracts(contractIds, options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create workflow job
      const job = await this.createWorkflowJob({
        jobType: 'batch_contract_processing',
        jobName: `معالجة دفعة العقود - ${new Date()?.toLocaleDateString('ar-JO')}`,
        totalItems: contractIds?.length,
        configuration: options
      });

      // Start processing
      await this.updateWorkflowJobStatus(job?.id, 'running');

      const results = [];
      const errors = [];

      for (const contractId of contractIds) {
        try {
          // Create batch operation record
          const { data: batchOp } = await supabase?.from('batch_operations')?.insert({
              workflow_job_id: job?.id,
              item_id: contractId,
              item_type: 'contract',
              operation_type: 'review_and_process',
              status: 'running'
            })?.select()?.single();

          // Fetch contract data
          const { data: contract } = await supabase?.from('contracts')?.select('*, properties(*), clients(*)')?.eq('id', contractId)?.single();

          if (!contract) {
            throw new Error('Contract not found');
          }

          // AI Review with Anthropic
          let reviewResult = null;
          if (options?.enableAIReview) {
            reviewResult = await autoReviewContract(
              contract?.terms || 'عقد قياسي',
              contract?.contract_type || 'rental',
              'Jordan'
            );
          }

          // Auto-approve if configured
          if (options?.autoApprove && reviewResult?.complianceScore >= 80) {
            await supabase?.from('contracts')?.update({ status: 'approved' })?.eq('id', contractId);
          }

          // Update batch operation
          await supabase?.from('batch_operations')?.update({
              status: 'completed',
              output_data: { reviewResult },
              processed_at: new Date()?.toISOString()
            })?.eq('id', batchOp?.id);

          results?.push({
            contractId,
            success: true,
            reviewResult
          });
        } catch (error) {
          // Log error for this contract
          await supabase?.from('batch_operations')?.update({
              status: 'failed',
              error_message: error?.message,
              processed_at: new Date()?.toISOString()
            })?.eq('workflow_job_id', job?.id)?.eq('item_id', contractId);

          errors?.push({
            contractId,
            error: error?.message
          });
        }
      }

      // Complete job
      await this.updateWorkflowJobStatus(job?.id, 'completed', {
        results: { processed: results, errors }
      });

      return {
        jobId: job?.id,
        totalProcessed: results?.length,
        totalFailed: errors?.length,
        results,
        errors
      };
    } catch (error) {
      console.error('Error in batch contract processing:', error);
      throw error;
    }
  },

  /**
   * Bulk Document Generation
   * Generates multiple documents using AI templates
   */
  async generateBulkDocuments(items, documentType, options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create workflow job
      const job = await this.createWorkflowJob({
        jobType: 'bulk_document_generation',
        jobName: `إنشاء مستندات متعددة - ${documentType}`,
        totalItems: items?.length,
        configuration: { documentType, ...options }
      });

      await this.updateWorkflowJobStatus(job?.id, 'running');

      const results = [];
      const errors = [];

      for (const item of items) {
        try {
          // Create batch operation
          const { data: batchOp } = await supabase?.from('batch_operations')?.insert({
              workflow_job_id: job?.id,
              item_id: item?.id,
              item_type: documentType,
              operation_type: 'generate_document',
              status: 'running',
              input_data: item
            })?.select()?.single();

          let generatedDocument = null;

          // Generate document based on type
          if (documentType === 'marketing_contract') {
            try {
              generatedDocument = await generateMarketingContractTemplate({
                clientName: item?.clientName,
                clientId: item?.clientId,
                clientPhone: item?.clientPhone,
                clientEmail: item?.clientEmail,
                propertyTitle: item?.propertyTitle,
                propertyType: item?.propertyType,
                propertyAddress: item?.propertyAddress,
                propertyCity: item?.propertyCity,
                propertyArea: item?.propertyArea,
                targetPrice: item?.targetPrice,
                minimumPrice: item?.minimumPrice,
                arddjoRetainerPercentage: item?.arddjoRetainerPercentage || 2.5,
                ownerRetainerPercentage: item?.ownerRetainerPercentage || 2.5,
                marketingDurationMonths: item?.marketingDurationMonths || 6,
                exclusiveRights: item?.exclusiveRights !== false
              });
            } catch (genError) {
              // Update batch operation with error
              await supabase?.from('batch_operations')?.update({
                status: 'failed',
                error_message: genError?.message || 'فشل إنشاء المستند',
                completed_at: new Date()?.toISOString()
              })?.eq('id', batchOp?.id);
              
              results?.failed?.push({
                itemId: item?.id,
                error: genError?.message || 'فشل إنشاء المستند'
              });
              continue;
            }
          } else if (documentType === 'property_listing') {
            // Generate property listing with OpenAI
            const response = await openai?.chat?.completions?.create({
              model: 'gpt-4o-mini',
              messages: [
                {
                  role: 'system',
                  content: 'أنت خبير في كتابة إعلانات العقارات باللغة العربية. اكتب وصفاً جذاباً واحترافياً للعقار.'
                },
                {
                  role: 'user',
                  content: `اكتب إعلان عقاري احترافي للعقار التالي:\n\nالعنوان: ${item?.title}\nالنوع: ${item?.propertyType}\nالموقع: ${item?.location}\nالمساحة: ${item?.area}\nالسعر: ${item?.price}\nالمميزات: ${item?.features?.join('، ') || 'غير محدد'}`
                }
              ],
              max_tokens: 500
            });

            generatedDocument = {
              html: `<div dir="rtl">${response?.choices?.[0]?.message?.content}</div>`,
              text: response?.choices?.[0]?.message?.content
            };
          }

          // Export to PDF if requested
          if (options?.exportToPDF && generatedDocument?.html) {
            await exportContractToPDF(generatedDocument?.html, {
              contractType: documentType,
              partyB: item?.clientName || item?.title
            });
          }

          // Update batch operation
          await supabase?.from('batch_operations')?.update({
              status: 'completed',
              output_data: { document: generatedDocument },
              processed_at: new Date()?.toISOString()
            })?.eq('id', batchOp?.id);

          results?.push({
            itemId: item?.id,
            success: true,
            document: generatedDocument
          });
        } catch (error) {
          await supabase?.from('batch_operations')?.update({
              status: 'failed',
              error_message: error?.message,
              processed_at: new Date()?.toISOString()
            })?.eq('workflow_job_id', job?.id)?.eq('item_id', item?.id);

          errors?.push({
            itemId: item?.id,
            error: error?.message
          });
        }
      }

      await this.updateWorkflowJobStatus(job?.id, 'completed', {
        results: { generated: results, errors }
      });

      return {
        jobId: job?.id,
        totalGenerated: results?.length,
        totalFailed: errors?.length,
        results,
        errors
      };
    } catch (error) {
      console.error('Error in bulk document generation:', error);
      throw error;
    }
  },

  /**
   * Automated Follow-up Scheduling
   * Creates follow-up tasks based on contract status and client interactions
   */
  async scheduleAutomatedFollowUps(criteria = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create workflow job
      const job = await this.createWorkflowJob({
        jobType: 'automated_follow_up',
        jobName: `جدولة متابعات تلقائية - ${new Date()?.toLocaleDateString('ar-JO')}`,
        totalItems: 0,
        configuration: criteria
      });

      await this.updateWorkflowJobStatus(job?.id, 'running');

      const followUps = [];
      const errors = [];

      // Find contracts needing follow-up
      let query = supabase?.from('contracts')?.select('*, clients(*), properties(*)')?.eq('user_id', user?.id);

      if (criteria?.status) {
        query = query?.eq('status', criteria?.status);
      }

      if (criteria?.contractType) {
        query = query?.eq('contract_type', criteria?.contractType);
      }

      const { data: contracts } = await query;

      // Update total items
      await supabase?.from('workflow_jobs')?.update({ total_items: contracts?.length })?.eq('id', job?.id);

      for (const contract of contracts) {
        try {
          // Create batch operation
          const { data: batchOp } = await supabase?.from('batch_operations')?.insert({
              workflow_job_id: job?.id,
              item_id: contract?.id,
              item_type: 'contract',
              operation_type: 'schedule_follow_up',
              status: 'running'
            })?.select()?.single();

          // Generate AI follow-up suggestion
          const response = await openai?.chat?.completions?.create({
            model: 'gpt-4o-mini',
            messages: [
              {
                role: 'system',
                content: 'أنت مساعد ذكي لإدارة المتابعات العقارية. اقترح موعد ورسالة متابعة مناسبة.'
              },
              {
                role: 'user',
                content: `العقد: ${contract?.contract_number}\nالحالة: ${contract?.status}\nالعميل: ${contract?.clients?.name}\nتاريخ البدء: ${contract?.start_date}\nتاريخ الانتهاء: ${contract?.end_date}\n\nاقترح موعد متابعة مناسب ورسالة متابعة احترافية.`
              }
            ],
            max_tokens: 300
          });

          const suggestion = response?.choices?.[0]?.message?.content;

          // Calculate follow-up date (default: 7 days from now)
          const followUpDate = new Date();
          followUpDate?.setDate(followUpDate?.getDate() + (criteria?.daysAhead || 7));

          // Create appointment for follow-up
          const { data: appointment } = await supabase?.from('appointments')?.insert({
              user_id: user?.id,
              client_id: contract?.client_id,
              property_id: contract?.property_id,
              title: `متابعة عقد ${contract?.contract_number}`,
              description: suggestion,
              appointment_date: followUpDate?.toISOString(),
              duration: 30,
              status: 'scheduled'
            })?.select()?.single();

          // Update batch operation
          await supabase?.from('batch_operations')?.update({
              status: 'completed',
              output_data: { appointmentId: appointment?.id, suggestion },
              processed_at: new Date()?.toISOString()
            })?.eq('id', batchOp?.id);

          followUps?.push({
            contractId: contract?.id,
            appointmentId: appointment?.id,
            followUpDate: followUpDate?.toISOString(),
            suggestion
          });
        } catch (error) {
          await supabase?.from('batch_operations')?.update({
              status: 'failed',
              error_message: error?.message,
              processed_at: new Date()?.toISOString()
            })?.eq('workflow_job_id', job?.id)?.eq('item_id', contract?.id);

          errors?.push({
            contractId: contract?.id,
            error: error?.message
          });
        }
      }

      await this.updateWorkflowJobStatus(job?.id, 'completed', {
        results: { followUps, errors }
      });

      return {
        jobId: job?.id,
        totalScheduled: followUps?.length,
        totalFailed: errors?.length,
        followUps,
        errors
      };
    } catch (error) {
      console.error('Error in automated follow-up scheduling:', error);
      throw error;
    }
  },

  /**
   * Create scheduled task for recurring automation
   */
  async createScheduledTask(taskData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('scheduled_tasks')?.insert({
          user_id: user?.id,
          task_name: taskData?.taskName,
          task_type: taskData?.taskType,
          frequency: taskData?.frequency || 'once',
          schedule_config: taskData?.scheduleConfig || {},
          next_run_at: taskData?.nextRunAt
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        taskName: data?.task_name,
        taskType: data?.task_type,
        frequency: data?.frequency,
        scheduleConfig: data?.schedule_config,
        isActive: data?.is_active,
        nextRunAt: data?.next_run_at,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating scheduled task:', error);
      throw error;
    }
  },

  /**
   * Get scheduled tasks
   */
  async getScheduledTasks() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('scheduled_tasks')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(task => ({
        id: task?.id,
        taskName: task?.task_name,
        taskType: task?.task_type,
        frequency: task?.frequency,
        scheduleConfig: task?.schedule_config,
        isActive: task?.is_active,
        lastRunAt: task?.last_run_at,
        nextRunAt: task?.next_run_at,
        runCount: task?.run_count,
        createdAt: task?.created_at
      }));
    } catch (error) {
      console.error('Error fetching scheduled tasks:', error);
      throw error;
    }
  },

  /**
   * Toggle scheduled task active status
   */
  async toggleScheduledTask(taskId, isActive) {
    try {
      const { data, error } = await supabase?.from('scheduled_tasks')?.update({ is_active: isActive })?.eq('id', taskId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling scheduled task:', error);
      throw error;
    }
  }
};